// Librairies
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, Signal, SimpleChanges, untracked, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { LabelService } from 'src/app/services/api/labels/label.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { Attachment, PutAttachment } from 'src/app/interfaces/api/attachment';
import { Label } from 'src/app/interfaces/api/label';
import { EnableTag, Project } from 'src/app/interfaces/api/project';

// Classes

// Components
import { AlertComponent, ButtonComponent, ConfirmComponent } from 'ui-library';

export interface DialogData {
  update: Function;
}

export interface TagRow {
  name: string;
  module: string;
  displayName: string;
  defaultDisplayName: string;
  enabled: boolean;
  editable: boolean;
  hidden?: boolean;
  count?: number;
  labelId?: string;
}

const TAG_MODULES: string[] = ['crane', 'risk'];

@Component({
  selector: 'app-prediction-tags',
  templateUrl: './prediction-tags.component.html',
  styleUrls: ['./prediction-tags.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    TranslateModule,
    FormsModule,
    ButtonComponent
  ]
})

export class PredictionTagsComponent implements OnInit, OnDestroy, OnChanges {
  // Services
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly attachmentsService: AttachmentService = inject(AttachmentService);
  private readonly labelService: LabelService = inject(LabelService);
  private readonly auth: AuthService = inject(AuthService);
  public readonly _data: any = inject(MAT_DIALOG_DATA, { optional: true });

  // Components API
  @Input() module: string = "crane";
  @Output() update: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('tagsPaginator') tagsPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Signals
  private readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  public readonly isPrivileged: Signal<boolean> = this.auth.isPrivileged;

  // Variables
  tagNames!: any[];
  project: any;
  newTag!: string;
  list: TagRow[] = [];
  taggedCount = 0;
  selectedTag: any;
  mergeInProgressTag: any;
  isMerging: boolean = false;
  isMergeInProgress: boolean = false;
  destroy$ = new Subject<void>();

  tagsData!: MatTableDataSource<TagRow>;
  tagsColumns: string[] = ['enable', 'name', 'displayName', 'countTag', 'action'];
  tagsCount: any;
  tags: any[] = [];
  selectAllEnabled!: boolean;
  loading: boolean = false;
  session: Record<string, string> = {};
  expiryDate: Date = new Date();
  data!: DialogData;

  private labels: Record<string, Label[]> = {};
  private labelNames: Record<string, Set<string>> = {};
  private stats: any[] = [];

  constructor() {
    this.project = untracked(() => this.currentProject());

    effect(() => {
      const project = this.currentProject();
      const previousId = this.project?.id;

      this.project = project;

      if (previousId && previousId !== project.id) {
        untracked(() => this.loadTags());
      }
    });

    if (this._data)
      this.data = this._data;
  }

  ngOnInit(): void {
    this.loadTags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['module'] && !changes['module'].firstChange) {
      this.loadTags()
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTags(): void {
    this.list = [];
    this.tagNames = [];
    this.loading = true;
    this.tagsData = new MatTableDataSource<TagRow>([]);

    forkJoin({
      stats: this.attachmentsService.getTagStats().pipe(catchError(() => of([] as any[]))),
      labels: this.labelService.getLabels({ project: TAG_MODULES.join(',') })
        .pipe(catchError(() => of({} as Record<string, Label[]>)))
    }).pipe(takeUntil(this.destroy$)).subscribe(({ stats, labels }) => {
      this.stats = Array.isArray(stats) ? stats : [];
      this.labels = {};
      this.labelNames = {};

      TAG_MODULES.forEach((module: string) => {
        const moduleLabels = (labels as Record<string, Label[]>)[module];

        this.labels[module] = Array.isArray(moduleLabels) ? moduleLabels : [];
        this.labelNames[module] = new Set(this.labels[module]!.map((label: Label) => label.name.toLowerCase()));
      });

      this.render();
      this.loading = false;
    });
  }

  private render(): void {
    const labels = this.labels[this.module] ?? [];

    this.tagNames = labels.map((label: Label) => ({
      name: label.name,
      module: this.module,
      displayName: label.displayName,
      editable: false
    })).sort((a, b) => a.name.localeCompare(b.name));

    this.list = this.buildList(labels);
    this.applyStats();
    this.reconcileEnableTags();
    this.applyEnableTags();
    this.refreshDataSource();
  }

  private buildList(labels: Label[]): TagRow[] {
    const rows: TagRow[] = this.customTagNames().map((name: string) => ({
      name,
      module: this.module,
      displayName: '',
      defaultDisplayName: '',
      enabled: true,
      editable: true
    }));

    const seen = new Set<string>(rows.map((row: TagRow) => row.name.toLowerCase()));

    labels
      .slice()
      .sort((a: Label, b: Label) => a.name.localeCompare(b.name))
      .forEach((label: Label) => {
        if (seen.has(label.name.toLowerCase())) return;

        seen.add(label.name.toLowerCase());

        const defaultDisplayName = this.isOwnDisplayName(label.name, label.displayName)
          ? label.displayName!.trim()
          : '';

        rows.push({
          name: label.name,
          module: this.module,
          displayName: defaultDisplayName,
          defaultDisplayName,
          enabled: true,
          editable: false,
          labelId: label.id
        });
      });

    return rows;
  }

  private applyStats(): void {
    this.taggedCount = 0;

    if (!this.stats.length) return;

    this.list.forEach((row: TagRow) => {
      const stat = this.stats.find(s => s && s._id && String(s._id).toLowerCase() == row.name.toLowerCase());

      if (!stat) return;

      row.count = stat.count;
      this.taggedCount += stat.count;
    });
  }

  private customTagNames(): string[] {
    if (this.module != "crane") return [];

    return (this.project?.predictionTags ?? []) as string[];
  }

  private enableTags(): EnableTag[] {
    if (!Array.isArray(this.project.enableTags))
      this.project.enableTags = [];

    return this.project.enableTags as EnableTag[];
  }

  private customNames(): Set<string> {
    return new Set(((this.project?.predictionTags ?? []) as string[]).map((name: string) => name.toLowerCase()));
  }

  private findEnableTag(name: string, module: string = this.module): EnableTag | undefined {
    return this.enableTags().find((tag: EnableTag) => tag.name == name && tag.module == module);
  }

  private reconcileEnableTags(): void {
    const enableTags = this.enableTags();
    let changed = this.migrateLegacyEnableTags();

    if (this.pruneMissingEnableTags())
      changed = true;

    this.list.forEach((row: TagRow) => {
      if (this.findEnableTag(row.name)) return;

      enableTags.push({ name: row.name, module: this.module, enabled: true, displayName: '' });
      changed = true;
    });

    if (changed)
      this.persist();
  }

  private migrateLegacyEnableTags(): boolean {
    const enableTags = this.enableTags();
    const legacy = enableTags.filter((tag: EnableTag) => !tag.module);

    if (!legacy.length) return false;

    const custom = this.customNames();

    legacy.forEach((tag: EnableTag) => {
      let modules = TAG_MODULES.filter((module: string) => this.labelNames[module]?.has(tag.name.toLowerCase()));

      if (!modules.length && custom.has(tag.name.toLowerCase()))
        modules = ['crane'];

      modules.forEach((module: string) => {
        if (this.findEnableTag(tag.name, module)) return;

        enableTags.push({
          name: tag.name,
          module,
          enabled: tag.enabled !== false,
          displayName: tag.displayName ?? '',
          ...(tag.hidden !== undefined && { hidden: tag.hidden })
        });
      });

      enableTags.splice(enableTags.indexOf(tag), 1);
    });

    return true;
  }

  private pruneMissingEnableTags(): boolean {
    const enableTags = this.enableTags();
    const custom = this.customNames();
    let changed = false;

    for (let i = enableTags.length - 1; i >= 0; i--) {
      const tag = enableTags[i] as EnableTag;
      const names = this.labelNames[tag.module];

      // Empty list means a partial or failed response, prune nothing.
      if (!names || !names.size) continue;
      if (names.has(tag.name.toLowerCase())) continue;
      if (tag.module == 'crane' && custom.has(tag.name.toLowerCase())) continue;

      enableTags.splice(i, 1);
      changed = true;
    }

    return changed;
  }

  // The API derives displayName from name when a label has none.
  private isOwnDisplayName(name: string, value?: string): boolean {
    const trimmed = (value ?? '').trim();

    return !!trimmed && trimmed != name;
  }

  private storedDisplayName(row: TagRow): string {
    const trimmed = (row.displayName ?? '').trim();

    if (!this.isOwnDisplayName(row.name, trimmed)) return '';

    return trimmed == row.defaultDisplayName ? '' : trimmed;
  }

  private applyEnableTags(): void {
    this.list.forEach((row: TagRow) => {
      const enableTag = this.findEnableTag(row.name);

      if (!enableTag) return;

      row.enabled = enableTag.enabled !== false;

      if (this.isOwnDisplayName(row.name, enableTag.displayName))
        row.displayName = enableTag.displayName.trim();

      if (enableTag.hidden !== undefined)
        row.hidden = enableTag.hidden;
    });
  }

  private refreshDataSource(): void {
    this.tagsData = new MatTableDataSource(this.list);
    this.tagsData.paginator = this.tagsPaginator;
    this.tagsData.sort = this.sort;
    this.checkIfAllSelected();
  }

  isTagEnabled(tagName: string): boolean {
    const foundTag = this.findEnableTag(tagName);
    return foundTag ? foundTag.enabled : false;
  }

  toggleTagEnable(tag: TagRow) {
    const matchingEnableTag = this.findEnableTag(tag.name);

    if (matchingEnableTag)
      matchingEnableTag.enabled = tag.enabled;
    else
      this.enableTags().push({ name: tag.name, module: this.module, enabled: tag.enabled, displayName: this.storedDisplayName(tag) });

    this.persist();
    this.checkIfAllSelected();
  }

  onDisplayNameChange() {
    this.saveTags();
  }

  toggleSelectAll(event: any) {
    this.selectAllEnabled = event.checked;

    this.tagsData.data.forEach((tag: TagRow) => {
      tag.enabled = this.selectAllEnabled;

      const matchingEnableTag = this.findEnableTag(tag.name);

      if (matchingEnableTag)
        matchingEnableTag.enabled = tag.enabled;
      else
        this.enableTags().push({ name: tag.name, module: this.module, enabled: tag.enabled, displayName: this.storedDisplayName(tag) });
    });

    this.persist();
  }

  checkIfAllSelected() {
    this.selectAllEnabled = this.tagsData.data.length > 0 && this.tagsData.data.every((tag: TagRow) => tag.enabled);
  }

  addTag() {
    if (!this.newTag || this.newTag.length < 2) return;

    const name = this.newTag;
    this.newTag = "";

    if (this.list.some((row: TagRow) => row.name.toLowerCase() == name.toLowerCase())) return;

    this.list = ([{
      name,
      module: this.module,
      displayName: '',
      defaultDisplayName: '',
      enabled: true,
      editable: true
    }] as TagRow[]).concat(this.list);

    if (!Array.isArray(this.project.predictionTags))
      this.project.predictionTags = [];

    this.project.predictionTags.push(name);

    this.saveTags();
    this.refreshDataSource();
  }

  removeTag(tag: string) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '300px',
      data: {
        title: "Supprimer le tag",
        content: `Voulez-vous vraiment supprimer le tag "${tag}" ?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.ok) {
        this.forget(tag);
        this.saveTags();
        this.refreshDataSource();
      }
    });
  }

  private forget(name: string) {
    const rowIndex = this.list.findIndex((row: TagRow) => row.name == name);
    if (rowIndex !== -1) this.list.splice(rowIndex, 1);

    const predictionTags = (this.project.predictionTags ?? []) as string[];
    const predictionIndex = predictionTags.findIndex((t: string) => t == name);
    if (predictionIndex !== -1) predictionTags.splice(predictionIndex, 1);

    const enableTags = this.enableTags();
    const enableTagIndex = enableTags.findIndex((t: EnableTag) => t.name == name && t.module == this.module);
    if (enableTagIndex !== -1) enableTags.splice(enableTagIndex, 1);
  }

  mergeTag(tag: any) {
    this.isMergeInProgress = true;
    this.mergeInProgressTag = tag;
    this.selectedTag = null;
  }

  cancelMerge() {
    this.isMergeInProgress = false;
    this.mergeInProgressTag = null;
    this.selectedTag = null;
  }

  confirmMerge(tag: any, training: boolean) {
    if (!this.selectedTag || tag.name == this.selectedTag.name) {
      const dialogRef2 = this.dialog.open(AlertComponent, {
        width: '400px',
        height: '140px',
        data: { title: "Veuillez selectionner un tag à fusionner", ok: false }
      });

      dialogRef2.afterClosed().subscribe(result => {
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '300px',
      data: {
        title: "Fusionner les tags",
        content: `Voulez-vous vraiment fusionner le tag "${tag.name}" avec le tag "${this.selectedTag.name}" ?`
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result.dismissed) {
        this.forget(tag.name);

        this.saveTags();
        this.refreshDataSource();

        const oldTag = tag.name || tag.tagName
        const newTag = this.selectedTag.name;

        this.attachmentsService.searchAttachments('', { tagName: oldTag, ...this.session })
          .pipe(takeUntil(this.destroy$)).subscribe(data => {
            data = data.map(a => {
              a.predictions?.forEach(prediction => {
                if (prediction.tagName == oldTag)
                  prediction.tagName = newTag;
              });

              if (a.tags?.crane)
                a.tags.crane.forEach(p => {
                  if (p.tagName == oldTag)
                    p.tagName = newTag;
                })

              return a;
            });

            const updatedAttachments: PutAttachment[] = data.map((attachment: Attachment) => ({
              id: attachment._id || attachment.id,
              url: attachment.url,
              name: attachment.name,
              oldTag: oldTag,
              newTag: newTag,
              training: training,
              predictions: attachment.predictions,
              tags: attachment.tags,
              customVisionIds: attachment.customVisionIds,
              processed: attachment.processed
            }));

            this.attachmentsService.updateManyAttachment(updatedAttachments)
              .pipe(takeUntil(this.destroy$)).subscribe(response => {
                if (this.data && this.data.update)
                  this.data.update(updatedAttachments)
                else
                  this.update.emit({ attachments: updatedAttachments });
                // this.dialogRef.close()
              });
          });
      }

      this.isMergeInProgress = false;
      this.isMerging = false;
      this.mergeInProgressTag = null;
      this.selectedTag = null;
    });
  }

  saveTags() {
    const others = this.enableTags().filter((t: EnableTag) => t.module != this.module);

    const current: EnableTag[] = this.list.map((row: TagRow) => ({
      name: row.name,
      module: this.module,
      enabled: row.enabled !== false,
      displayName: this.storedDisplayName(row),
      ...(row.hidden !== undefined && { hidden: row.hidden })
    }));

    this.project.enableTags = others.concat(current);

    if (this.module == "crane")
      this.project.predictionTags = this.list.filter((row: TagRow) => row.editable).map((row: TagRow) => row.name);

    this.persist();
  }

  private persist() {
    const json: any = {
      id: this.project.id,
      enableTags: this.project.enableTags
    };

    if (this.module == "crane")
      json.predictionTags = this.project.predictionTags ?? [];

    this.projectService.setProject(json).pipe(takeUntil(this.destroy$)).subscribe(res => {
      // New reference, otherwise the signal notifies nothing.
      this.projectService.setCurrentProject({ ...this.project });
    });
  }
}
